import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { getDoc, doc } from "firebase/firestore"; // Added for role lookup
import { auth, db } from "./firebase"; // Imported db reference

const PrivateRoute = ({ children, allowedRoles }) => {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null); // Added role state tracking
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      
      if (currentUser) {
        try {
          // 🔍 Fetch user's role directly from your Firestore collection
          const userSnap = await getDoc(doc(db, "users", currentUser.uid));
          if (userSnap.exists()) {
            setUserRole(userSnap.data().role || null);
          }
        } catch (err) {
          console.error("Error fetching user role inside route guard:", err);
          setUserRole(null);
        }
      } else {
        setUserRole(null);
      }
      
      setLoading(false);
    });
    
    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50 select-none">
        <div className="p-6 bg-white shadow-xl rounded-xl text-center border border-gray-100">
          <h2 className="text-sm font-bold text-gray-700 animate-pulse">
            ⏳ Authenticating secure profile parameters...
          </h2>
        </div>
      </div>
    );
  }

  // 🚨 Gate 1: Block if not logged in
  if (!user) return <Navigate to="/login" replace />;

  // 🚨 Gate 2: Block if email not verified
  if (!user.emailVerified) return <Navigate to="/verify-email" replace />;

  // 🚨 Gate 3: Authorization Check (Role Guard Rule)
  // If a route specifies allowedRoles, check if the current user's role matches
  if (allowedRoles && allowedRoles.length > 0) {
    if (!allowedRoles.includes(userRole)) {
      console.warn(`🔒 Access denied. Role "${userRole}" is not authorized for this workspace.`);
      
      // Kick them back to their appropriate dashboard home instead of crashing
      if (userRole === "investor") return <Navigate to="/investor-dashboard" replace />;
      return <Navigate to="/dashboard" replace />;
    }
  }

  // ✅ Gate 4: Allow access if all security criteria are satisfied
  return children;
};

export default PrivateRoute;
